<template>
  <app-base-sheet ref="sheet">
    <template v-slot:title>Фільтр</template>
    <template v-slot:content>
      <div class="goods-filters">
        <div class="goods-filters__section">
          <app-checkbox v-model="newFilters.hasDiscount">
            Товари зі знижкою
          </app-checkbox>
        </div>

        <div class="goods-filters__section">
          <h2>Категорії</h2>
          <app-checkbox
            v-for="category in availableCategories"
            :key="category.id"
            :value="category.id"
            v-model="newFilters.categories"
          >
            {{ category.name }}
          </app-checkbox>
        </div>

        <div class="goods-filters__section border-0">
          <h2>Ціна</h2>
          <div class="row mt-3">
            <div class="col-12">
              <app-multi-range
                :from="from"
                :to="to"
                @update:from="(val) => (from = val)"
                @update:to="(val) => (to = val)"
                :min="minPrice"
                :max="maxPrice"
                :step="100"
                @focus="handleInputState($event, true)"
                @blur="handleInputState($event, false)"
              />
            </div>
          </div>
        </div>

        <div class="goods-filters__buttons">
          <app-btn @click="apply" class="btn btn-primary">
            Застосувати
          </app-btn>
          <app-btn @click="reset" class="btn btn-white">Скинути</app-btn>
        </div>
        <div
          v-show="isShowAdditionalSpace"
          ref="additionalSpace"
          class="additional-space"
        ></div>
      </div>
    </template>
  </app-base-sheet>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useStore } from "vuex";
import AppBaseSheet from "@/components/common/ui/AppBaseSheet.vue";
import AppMultiRange from "@/components/common/ui/AppMultiRange.vue";
import AppCheckbox from "@/components/common/ui/AppCheckbox.vue";
import AppBtn from "@/components/common/ui/AppBtn.vue";
import { eventBus, events } from "@/eventBus";
import { mutationTypes as shopMutationTypes } from "@/store/modules/shop";

export default {
  name: "ShopFilterSheet",
  components: {
    AppBtn,
    AppBaseSheet,
    AppMultiRange,
    AppCheckbox,
  },
  setup() {
    const store = useStore();
    const sheet = ref(null);
    const isShowAdditionalSpace = ref(false);
    const activeInputElement = ref(null);
    const additionalSpace = ref(null);

    const isMobilePlatform = computed(() => store.getters.isMobilePlatform);

    const availableCategories = computed(
      () => store.getters["shop/availableCategories"]
    );

    const availableProducts = computed(
      () => store.getters["shop/availableProducts"]
    );

    const setFilterParams = (payload) =>
      store.commit(`shop/${shopMutationTypes.SET_FILTER_PARAMS}`, payload);

    const maxPrice = computed(() =>
      availableProducts.value.length
        ? Math.max(...availableProducts.value.map((item) => item.price))
        : 5000
    );

    const minPrice = computed(() =>
      availableProducts.value.length
        ? Math.min(...availableProducts.value.map((item) => item.price))
        : 0
    );

    const currentFilters = computed(() => store.getters["shop/filterParams"]);

    const newFilters = ref({
      categories: [],
      hasDiscount: false,
      priceFrom: null,
      priceTo: null,
    });

    const from = ref(currentFilters.value.priceFrom ?? minPrice.value);
    const to = ref(currentFilters.value.priceTo ?? maxPrice.value);

    watch([from, to], ([newFrom, newTo]) => {
      newFilters.value.priceFrom = newFrom;
      newFilters.value.priceTo = newTo;
    });

    const handleInputState = (event, value) => {
      if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) return;
      if (value) {
        // focus
        activeInputElement.value = event.target;

        if (!isMobilePlatform.value) return;

        isShowAdditionalSpace.value = true;

        nextTick(() => {
          const inputRect = event?.target?.getBoundingClientRect?.();
          const spaceEl = additionalSpace.value;
          const spaceRect = spaceEl?.getBoundingClientRect?.();

          if (inputRect && spaceRect) {
            const distance =
              window.innerHeight - spaceRect.top + inputRect.top - 80;

            spaceEl.style.height = `${distance}px`;
          }

          nextTick(() => {
            const wrapper = sheet.value?.sheetWrapper;
            if (wrapper) {
              wrapper.scrollTo({
                top: wrapper.scrollHeight,
                behavior: "smooth",
              });
            }
          });
        });
      } else {
        // blur
        setTimeout(() => {
          // Якщо активний елемент не змінився на новий input — ховаємо
          if (event.target === activeInputElement.value) {
            isShowAdditionalSpace.value = false;
            activeInputElement.value = null;
          }
        }, 300);
      }
    };

    const apply = () => {
      setFilterParams({ ...newFilters.value });
      sheet.value?.close();
    };

    const reset = () => {
      newFilters.value = {
        categories: [],
        hasDiscount: false,
        priceFrom: null,
        priceTo: null,
      };
      from.value = minPrice.value;
      to.value = maxPrice.value;
      setFilterParams({ ...newFilters.value });
      sheet.value?.close();
    };

    onMounted(() => {
      eventBus.$on(events.OPEN_SHEET_SHOP_FILTERS, () => {
        newFilters.value = {
          categories: [...currentFilters.value.categories],
          hasDiscount: currentFilters.value.hasDiscount,
          priceFrom: currentFilters.value.priceFrom,
          priceTo: currentFilters.value.priceTo,
        };

        from.value = currentFilters.value.priceFrom ?? minPrice.value;
        to.value = currentFilters.value.priceTo ?? maxPrice.value;

        sheet.value?.open();
      });
    });

    onUnmounted(() => {
      eventBus.$off(events.OPEN_SHEET_SHOP_FILTERS);
    });

    return {
      sheet,
      availableCategories,
      availableProducts,
      additionalSpace,
      newFilters,
      from,
      to,
      maxPrice,
      minPrice,
      isShowAdditionalSpace,
      handleInputState,
      apply,
      reset,
    };
  },
};
</script>

<style scoped lang="less">
@import "@/styles/_variables.less";

.goods-filters {
  h2 {
    font-style: normal;
    font-weight: 500;
    font-size: 18px;
    line-height: 21px;
    letter-spacing: 0.005em;
    color: @white;
    margin-bottom: 22px;
  }

  &__section {
    margin-bottom: 24px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);

    &.border-0 {
      border-bottom: none;
    }
  }

  &__buttons {
    display: flex;
    flex-direction: column;
    gap: 15px;
    margin-top: 24px;
  }
}
</style>
